import React, { useEffect } from "react";
import { TextOverlayControls } from "./TextOverlayControls";
import { UnifiedOverlayControls } from "./UnifiedOverlayControls";

interface TextOverlay {
  id: string;
  text: string;
  fontSize: number;
  fontColor: string;
  backgroundColor: string;
  position: { x: number; y: number };
  visible: boolean;
  isBold?: boolean;
  isItalic?: boolean;
  isUnderline?: boolean;
  padding?: number;
}

interface LogoOverlay {
  id: string;
  url: string;
  position: { x: number; y: number };
  size: { width: number; height: number };
  visible: boolean;
}

interface SlateOverlay {
  id: string;
  url: string;
  position: { x: number; y: number };
  size: { width: number; height: number };
  visible: boolean;
  isSlate: boolean;
  duration?: number;
  autoHide?: boolean;
}

interface CanvasOverlayControlsProps {
  textOverlays: TextOverlay[];
  logoOverlays: LogoOverlay[];
  slateOverlays?: SlateOverlay[];
  onTextOverlaysChange: (textOverlays: TextOverlay[]) => void;
  onLogoOverlaysChange: (logoOverlays: LogoOverlay[]) => void;
  onSlateOverlaysChange?: (slateOverlays: SlateOverlay[]) => void;
  onLogoUpload?: (file: File) => void;
  showCanvasOverlay: boolean;
  onToggleCanvasOverlay: () => void;
  selectedTextOverlayId?: string;
  selectedLogoOverlayId?: string;
  onTextOverlaySelect?: (textOverlayId: string) => void;
  onLogoOverlaySelect?: (logoOverlayId: string) => void; // New prop for logo selection
  isPrePostRollMode?: boolean;
  onPreRollChange?: (media: any) => void;
  onPostRollChange?: (media: any) => void;
  preRollMedia?: any;
  postRollMedia?: any;
  preRollMediaList?: any[];
  postRollMediaList?: any[];
  onPreRollSearch?: (params: { stitle: string; key: string }) => void;
  onPostRollSearch?: (params: { stitle: string; key: string }) => void;
  onClearPreRollMedia?: () => void;
  onClearPostRollMedia?: () => void;
  isPreset?: boolean;
  callRegisterMediaOnCms?: any;
  ImageUpload?: any;
  isPreview?: boolean;
  currentMedia?: any;
  handlePresetId?: (presetId: string) => void;
  presetList?: any[];
  readOnlyMode?: boolean;
}

export const CanvasOverlayControls: React.FC<CanvasOverlayControlsProps> = ({
  textOverlays,
  logoOverlays,
  slateOverlays = [],
  onTextOverlaysChange,
  onLogoOverlaysChange,
  onSlateOverlaysChange,
  onLogoUpload,
  showCanvasOverlay,
  onToggleCanvasOverlay,
  selectedTextOverlayId,
  selectedLogoOverlayId,
  onTextOverlaySelect,
  onLogoOverlaySelect,
  isPrePostRollMode = false,
  onPreRollChange,
  onPostRollChange,
  preRollMedia,
  postRollMedia,
  preRollMediaList = [],
  postRollMediaList = [],
  onPreRollSearch,
  onPostRollSearch,
  onClearPreRollMedia,
  onClearPostRollMedia,
  isPreset = false,
  callRegisterMediaOnCms,
  ImageUpload,
  currentMedia,
  handlePresetId,
  presetList,
  isPreview,
  readOnlyMode = false,
}) => {
  useEffect(() => {
    if (!showCanvasOverlay) {
      onToggleCanvasOverlay();
    }
  }, []);

  return (
    <div className="canvas-overlay-controls">
      <div className="overlay-sections">
        {onSlateOverlaysChange && (
          <UnifiedOverlayControls
            overlayType="slate"
            slateOverlays={slateOverlays}
            onSlateOverlaysChange={onSlateOverlaysChange}
            isPrePostRollMode={isPrePostRollMode}
            onPreRollChange={onPreRollChange}
            onPostRollChange={onPostRollChange}
            preRollMedia={preRollMedia}
            postRollMedia={postRollMedia}
            preRollMediaList={preRollMediaList}
            postRollMediaList={postRollMediaList}
            onPreRollSearch={onPreRollSearch}
            onPostRollSearch={onPostRollSearch}
            onClearPreRollMedia={onClearPreRollMedia}
            onClearPostRollMedia={onClearPostRollMedia}
            isPreset={isPreset}
            handlePresetId={handlePresetId}
            presetList={presetList}
            readOnlyMode={readOnlyMode}
          />
        )}

        <TextOverlayControls
          textOverlays={textOverlays}
          onTextOverlaysChange={onTextOverlaysChange}
          selectedTextOverlayId={selectedTextOverlayId}
          onTextOverlaySelect={onTextOverlaySelect}
          isPreview={isPreview}
        />

        {onLogoOverlaysChange && (
          <UnifiedOverlayControls
            overlayType="logo"
            logoOverlays={logoOverlays}
            onLogoOverlaysChange={onLogoOverlaysChange}
            onLogoUpload={onLogoUpload}
            isPrePostRollMode={isPrePostRollMode}
            onPreRollChange={onPreRollChange}
            onPostRollChange={onPostRollChange}
            preRollMedia={preRollMedia}
            postRollMedia={postRollMedia}
            preRollMediaList={preRollMediaList}
            postRollMediaList={postRollMediaList}
            onPreRollSearch={onPreRollSearch}
            onPostRollSearch={onPostRollSearch}
            onLogoOverlaySelect={onLogoOverlaySelect}
            selectedLogoOverlayId={selectedLogoOverlayId}
            isPreset={isPreset}
            callRegisterMediaOnCms={callRegisterMediaOnCms}
            ImageUpload={ImageUpload}
            currentMedia={currentMedia}
            handlePresetId={handlePresetId}
            presetList={presetList}
            isPreview={isPreview}
            readOnlyMode={readOnlyMode}
          />
        )}
      </div>
    </div>
  );
};

export default CanvasOverlayControls;
